import { HttpErrorResponse } from '@angular/common/http';
import { UpperCasePipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	EmptyStateComponent,
	Icons,
	IMenu,
	ItemComponent,
	ItemFilterComponent,
	ItemGroupComponent,
	ITagImage,
	IToolbar,
	InfoGroupComponent,
	LinkComponent,
	ListLayoutComponent,
	MenuComponent,
	PaginationComponent,
	TagComponent,
	TitleGroupComponent,
	ToolbarComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { Observable } from 'rxjs';
import { Permission } from '../../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { formatDate } from '../../../../../core/utils/utils';
import { environment } from '../../../../../../environments/environment';
import { buildCurlPostJson, CURL_BEARER_PLACEHOLDER } from '../../../../../panel/utils/build-curl.util';
import { MenuItemAction } from '../../../../../shared/models/master.models';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { ToolbarHelperService } from '../../../../../shared/services/toolbar-helper.service';
import { ModalCurlRequestComponent } from '../../../../../shared/components/modal-curl-request/modal-curl-request.component';
import { PaginatedListController } from '../../../../../shared/list/paginated-list.controller';
import { ICompaniesListBody, ICompaniesListPayload, ICompaniesListResponse } from '../../models/companies.models';
import { CompaniesHelperService } from '../../services/companies-helper.service';
import { CompaniesService } from '../../services/companies.service';
import { CompaniesDetailComponent } from '../companies-detail/companies-detail.component';
import { CompaniesFormComponent } from '../companies-form/companies-form.component';
import { ModalConfirmationService } from '../../../../../shared/services/modal-confirmation.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
	clearActivityUuidQueryParam,
	parseActivityDeepLinkUuid,
} from '../../../../../core/utils/settings-activity-deep-link.util';

@Component({
	selector: 'app-companies-list',
	imports: [
		TranslatePipe,
		UpperCasePipe,
		ListLayoutComponent,
		TitleGroupComponent,
		ButtonComponent,
		AlertComponent,
		ToolbarComponent,
		ItemFilterComponent,
		ItemGroupComponent,
		ItemComponent,
		TagComponent,
		LinkComponent,
		MenuComponent,
		ButtonIconComponent,
		PaginationComponent,
		EmptyStateComponent,
		InfoGroupComponent,
		CompaniesFormComponent,
		CompaniesDetailComponent,
		ModalCurlRequestComponent,
		WatermarkComponent,
	],
	templateUrl: './companies-list.component.html',
	styleUrl: './companies-list.component.scss',
})
export class CompaniesListComponent extends PaginatedListController<ICompaniesListPayload> implements OnInit {
	private toolbarHelperService = inject(ToolbarHelperService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private companiesService = inject(CompaniesService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private modalConfirmationService = inject(ModalConfirmationService);

	@ViewChild(ToolbarComponent) private toolbarComponent?: ToolbarComponent;
	private snackbarService = inject(SnackbarService);
	public companiesHelper = inject(CompaniesHelperService);
	public rolePermissions = inject(RolePermissionsService);

	protected readonly Permission = Permission;
	/** Placeholders de skeleton mientras carga el listado (NIF-745). */
	public readonly listSkeletonItems = [0, 1, 2, 3, 4] as const;
	public readonly curlTagImage: ITagImage = { icon: 'icon-code-brackets' as Icons };

	// LIST
	public toolbar: Signal<IToolbar>;
	public companiesList: ICompaniesListBody[];
	public override filters?: ICompaniesListPayload = { enabled: true };
	public override config: { pagination: import('@tecalisdev/infinity').ITablePagination } =
		this.companiesHelper.config;

	// GENERAL
	public showForm: boolean = false;
	public showDetail: boolean = false;
	public showModalCurl: boolean = false;
	public selectedCompanyCurl: string = '';
	public curlModalTitleKey = 'PANEL.CURL_MODAL.TITLE_COMPANY_DETAIL';
	public companySelected: ICompaniesListBody;
	public menuOptionsMap: Map<string, IMenu[]> = new Map();
	private pendingActivityUuid: string | null = null;
	private deepLinkSearchRetried = false;

	constructor() {
		super();
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (modalResponse?.id?.menuAction === MenuItemAction.STATUS) {
				const company: ICompaniesListBody = modalResponse?.id?.company;
				if (modalResponse?.action === 'buttonPrimary') {
					this.updateStatusCompany(company);
				}
			} else if (modalResponse?.id?.menuAction === MenuItemAction.DELETE) {
				if (modalResponse?.action === 'buttonPrimary') {
					const company: ICompaniesListBody = modalResponse?.id?.company;
					this.confirmDeleteCompany(company);
				}
			}
		});
	}

	ngOnInit(): void {
		this.pendingActivityUuid = parseActivityDeepLinkUuid(this.route);
		this.toolbarHelperService.initializeListToolbar(this, () => this.companiesHelper.toolbar);

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.config = {
				pagination: {
					...this.config.pagination,
					label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				},
			};
			if (this.toolbarComponent) {
				const currentEnabled =
					typeof this.filters?.enabled === 'boolean'
						? this.filters.enabled
						: (this.filters?.enabled as any)?.value;
				if (currentEnabled !== undefined && currentEnabled !== null) {
					this.toolbarComponent.form
						.get('enabled')
						?.setValue(currentEnabled ? 'true' : 'false', { emitEvent: false });
				}
			}
		});
	}

	protected createListRequest(): Observable<ICompaniesListResponse> {
		this.formatFilters();
		// formatFilterTable (via withPagination) unwraps select option → id; resolve sort after that
		// so toolbar objects like { id: 'asc', value: 'asc' } are not overwritten by a blind 'desc'.
		const paginated = this.withPagination(this.filters);
		const sortOrder =
			paginated.sort_order === 'asc' || paginated.sort_order === 'desc' ? paginated.sort_order : 'desc';
		this.filters = {
			...paginated,
			sort_by: 'created_at',
			sort_order: sortOrder,
		};
		return this.companiesService.list(this.filters);
	}

	protected applyListResponse(response: ICompaniesListResponse): void {
		this.menuOptionsMap.clear();
		this.companiesList = response.data.map((company) => this.formatCompaniesListItem(company));
		this.tryOpenCompanyFromActivityDeepLink();
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.alertError = this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
	}

	private formatCompaniesListItem(company: ICompaniesListBody): ICompaniesListBody {
		if (company.created_at) {
			company.created_at = formatDate(company.created_at);
		}
		if (company.code) {
			company.code = company.code.toUpperCase();
		}
		return company;
	}

	public getItemMenuOpt(company: ICompaniesListBody): IMenu[] {
		if (!this.menuOptionsMap.has(company.uuid)) {
			const options = [...this.companiesHelper.getDetailMenuOpt(company)];
			if (this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)) {
				const curlItem: IMenu = {
					label: this.trans.instant('COMPANIES.LIST.MENU_CURL_REQUEST'),
					icon: 'icon-code-brackets',
					id: MenuItemAction.SHOW_CURL,
				};
				const deleteIdx = options.findIndex((o) => o.id === MenuItemAction.DELETE);
				if (deleteIdx >= 0) {
					options.splice(deleteIdx, 0, curlItem);
				} else {
					options.push(curlItem);
				}
			}
			this.menuOptionsMap.set(company.uuid, options);
		}
		return this.menuOptionsMap.get(company.uuid);
	}

	public itemMenuChange(action: IMenu, company: ICompaniesListBody): void {
		const actions = {
			[MenuItemAction.STATUS]: () => {
				this.modalConfirmationService.show({
					title: this.trans.instant(
						`COMPANIES.LIST.${company.enabled ? 'UPDATE_MODAL_TITLE_INACTIVE' : 'UPDATE_MODAL_TITLE_ACTIVE'}`,
						{ alias: company.name },
					),
					description: '',
					buttonPrimaryLabel: this.trans.instant(`BUTTON.${company.enabled ? 'INACTIVATE' : 'ACTIVATE'}`),
					buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
					buttonPrimaryStyle: true,
					id: { company, menuAction: MenuItemAction.STATUS },
				});
			},
			[MenuItemAction.EDIT]: () => {
				this.editCompany(company);
			},
			[MenuItemAction.DELETE]: () => {
				this.modalConfirmationService.show({
					title: this.trans.instant('COMPANIES.MODAL_DELETE.TITLE'),
					description: this.trans.instant('COMPANIES.MODAL_DELETE.DESCRIPTION'),
					buttonPrimaryLabel: this.trans.instant('BUTTON.DELETE'),
					buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
					buttonPrimaryStyle: true,
					id: { company, menuAction: MenuItemAction.DELETE },
				});
			},
			[MenuItemAction.SHOW_CURL]: () => {
				this.openModalCurl(company);
			},
		};

		actions[action.id]?.();
	}

	private updateStatusCompany(company: ICompaniesListBody) {
		this.loading.show(this.trans.instant('LOADING.UPDATE_COMPANY'));
		this.companiesService
			.update(company?.uuid, {
				name: company.name,
				code: company.code,
				manual_validation: company.manual_validation,
				document_retention_days: company.document_retention_days,
				media_retention_days: company.media_retention_days,
				info_retention_days: company.info_retention_days,
				enabled: !company.enabled,
			})
			.subscribe({
				next: () => {
					this.getList();
					this.snackbarService.show(this.trans.instant('COMPANIES.LIST.UPDATE_CONFIRM'));
				},
				error: (_error) => {
					this.loading.hide();
					this.alertError = this.trans.instant('COMPANIES.LIST.UPDATE_ERROR');
				},
				complete: () => {},
			});
	}

	private confirmDeleteCompany(company: ICompaniesListBody): void {
		if (!company?.uuid) return;
		this.loading.show(this.trans.instant('LOADING.DELETE_COMPANY'), false, false);
		this.companiesService.delete(company.uuid).subscribe({
			next: () => {
				this.getList();
				this.snackbarService.show(this.trans.instant('COMPANIES.LIST.DELETE_CONFIRM'));
				this.companySelected = null;
			},
			error: (_error) => {
				this.loading.hide();
				this.alertError = this.trans.instant('COMPANIES.LIST.DELETE_ERROR');
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public goToDetail(company: ICompaniesListBody, event?: Event): void {
		event?.preventDefault?.();
		event?.stopPropagation?.();
		this.showDetail = true;
		this.companySelected = company;
	}

	private editCompany(company: ICompaniesListBody): void {
		this.showForm = true;
		this.companySelected = company;
	}

	private openModalCurl(company: ICompaniesListBody): void {
		if (!this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)) return;
		this.curlModalTitleKey = 'PANEL.CURL_MODAL.TITLE_COMPANY_DETAIL';
		this.selectedCompanyCurl = this.getCompanyDetailCurl(company.uuid);
		this.showModalCurl = true;
	}

	public openListCurlModal(): void {
		if (!this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)) return;
		this.curlModalTitleKey = 'PANEL.CURL_MODAL.TITLE_COMPANY_LIST';
		this.selectedCompanyCurl = this.getCompaniesListCurl();
		this.showModalCurl = true;
	}

	public onToolbarAction(action: string | { id?: string }): void {
		const id = typeof action === 'string' ? action : action?.id;
		if (id === MenuItemAction.SHOW_CURL) {
			this.openListCurlModal();
		}
	}

	public closeModalCurl(): void {
		this.showModalCurl = false;
		this.selectedCompanyCurl = '';
	}

	/**
	 * Formatea los filtros: vacíos, enabled (fastFilter string|boolean) y búsqueda nombre+código.
	 */
	private formatFilters(): void {
		if (!this.filters) return;

		this.clearEmptyFilters();
		if (Object.hasOwn(this.filters, 'enabled')) {
			const enabledRaw = (this.filters as any).enabled;
			if (enabledRaw !== null && enabledRaw !== undefined && enabledRaw !== '') {
				if (typeof enabledRaw === 'boolean') {
					this.filters.enabled = enabledRaw;
				} else if (enabledRaw === 'true' || enabledRaw === 'false') {
					this.filters.enabled = enabledRaw === 'true';
				} else if (enabledRaw?.value !== undefined) {
					this.filters.enabled = enabledRaw.value;
				} else if (enabledRaw?.id === 'true' || enabledRaw?.id === 'false') {
					this.filters.enabled = enabledRaw.id === 'true';
				}
			} else {
				delete this.filters.enabled;
			}
		}

		const searchTerm = this.filters.name?.trim();
		if (searchTerm) {
			this.filters.name = searchTerm;
			this.filters.code = searchTerm;
		} else {
			delete this.filters.code;
		}
	}

	private clearEmptyFilters(): void {
		if (!this.filters) return;

		Object.keys(this.filters).forEach((key) => {
			if (this.filters[key] === '' || this.filters[key] === null || this.filters[key] === undefined) {
				delete this.filters[key];
			}
		});
	}

	public getCompaniesListCurl(): string {
		if (!this.filters) return '';
		return buildCurlPostJson(environment.apiUrl, '/companies', this.filters as object);
	}

	public getCompanyDetailCurl(uuid: string | undefined): string {
		if (!uuid) return '';
		const base = environment.apiUrl.replace(/\/$/, '');
		const url = `${base}/company/${uuid}`;
		return [`curl -X GET "${url}" \\`, `  -H "Authorization: Bearer ${CURL_BEARER_PLACEHOLDER}"`].join('\n');
	}

	public goToCreate(): void {
		this.showForm = true;
		this.companySelected = null;
	}

	public closeForm(reload: boolean = false): void {
		if (reload) this.getList();
		this.showForm = false;
	}

	public closeDetail(): void {
		this.showDetail = false;
	}

	public editFromDetail(): void {
		const company = this.companySelected;
		this.showDetail = false;
		if (company) {
			this.editCompany(company);
		}
	}

	public onDetailMenuChange(action: IMenu): void {
		const company = this.companySelected;
		if (!company) {
			return;
		}
		if (
			action.id === MenuItemAction.EDIT ||
			action.id === MenuItemAction.DELETE ||
			action.id === MenuItemAction.STATUS
		) {
			this.showDetail = false;
		}
		this.itemMenuChange(action, company);
	}

	private tryOpenCompanyFromActivityDeepLink(): void {
		const uuid = this.pendingActivityUuid;
		if (!uuid || !this.rolePermissions.canEditCompany()) {
			this.finishActivityDeepLinkIfMatched(uuid);
			return;
		}
		const row = this.companiesList?.find((c: ICompaniesListBody) => c.uuid === uuid);
		if (row) {
			this.editCompany(row);
			this.finishActivityDeepLinkIfMatched(uuid);
			return;
		}
		if (!this.deepLinkSearchRetried) {
			this.deepLinkSearchRetried = true;
			this.filters = {
				...(this.filters || {}),
				name: uuid,
				page: 1,
			};
			this.config.pagination.page = 1;
			this.getList();
			return;
		}
		this.finishActivityDeepLinkIfMatched(uuid);
	}

	private finishActivityDeepLinkIfMatched(expectedUuid: string | null): void {
		if (!expectedUuid || this.pendingActivityUuid !== expectedUuid) {
			return;
		}
		this.pendingActivityUuid = null;
		this.deepLinkSearchRetried = false;
		clearActivityUuidQueryParam(this.router, this.route);
	}
}
